import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { supabase } from '@/integrations/supabase/client';
import { Activity, Users, Brain, TrendingUp, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface EngineComponent {
  id: string;
  component: string;
  last_run: string | null;
  last_status: string | null;
  run_count: number | null;
}

interface Stats {
  totalUsers: number;
  signalsToday: number;
  tradesToday: number;
}

function statusColor(lastRun: string | null): { label: string; className: string } {
  if (!lastRun) return { label: 'Never', className: 'bg-destructive/15 text-destructive border-destructive/20' };
  const age = Date.now() - new Date(lastRun).getTime();
  if (age < 10 * 60_000) return { label: 'Healthy', className: 'bg-[hsl(var(--success)/0.15)] text-[hsl(var(--success))] border-[hsl(var(--success)/0.2)]' };
  if (age < 60 * 60_000) return { label: 'Warning', className: 'bg-[hsl(var(--warning)/0.15)] text-[hsl(var(--warning))] border-[hsl(var(--warning)/0.2)]' };
  return { label: 'Stale', className: 'bg-destructive/15 text-destructive border-destructive/20' };
}

function timeAgo(iso: string | null): string {
  if (!iso) return '—';
  const sec = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (sec < 60) return `${sec}s ago`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
  return `${Math.floor(sec / 86400)}d ago`;
}

const AdminHealthCheck = () => {
  const [components, setComponents] = useState<EngineComponent[]>([]);
  const [stats, setStats] = useState<Stats>({ totalUsers: 0, signalsToday: 0, tradesToday: 0 });
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    setLoading(true);
    try {
      const todayStart = new Date();
      todayStart.setHours(0, 0, 0, 0);

      const [engineRes, usersRes, signalsRes, tradesRes] = await Promise.all([
        supabase.from('engine_health').select('*').order('component'),
        supabase.from('profiles').select('id', { count: 'exact', head: true }),
        supabase.from('ai_signals').select('id', { count: 'exact', head: true }).gte('created_at', todayStart.toISOString()),
        supabase.from('strategy_orders').select('id', { count: 'exact', head: true }).gte('created_at', todayStart.toISOString()),
      ]);

      setComponents(engineRes.data ?? []);
      setStats({
        totalUsers: usersRes.count ?? 0,
        signalsToday: signalsRes.count ?? 0,
        tradesToday: tradesRes.count ?? 0,
      });
    } catch (err) {
      console.error('Admin health fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30_000);
    return () => clearInterval(interval);
  }, []);

  const healthyCount = components.filter(c => {
    if (!c.last_run) return false;
    return Date.now() - new Date(c.last_run).getTime() < 10 * 60_000;
  }).length;

  return (
    <div className="p-4 lg:p-6 space-y-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold font-heading">Admin Health Check</h1>
          <p className="text-xs text-muted-foreground mt-0.5">Platform monitoring — auto-refreshes every 30s</p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchData} disabled={loading}>
          <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
              <Activity className="w-4 h-4 text-primary" />
            </div>
            <div>
              <p className="text-[11px] text-muted-foreground">Engine Health</p>
              <p className="text-lg font-bold tabular-nums">{healthyCount}/{components.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
              <Users className="w-4 h-4 text-primary" />
            </div>
            <div>
              <p className="text-[11px] text-muted-foreground">Total Users</p>
              <p className="text-lg font-bold tabular-nums">{stats.totalUsers}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
              <Brain className="w-4 h-4 text-primary" />
            </div>
            <div>
              <p className="text-[11px] text-muted-foreground">Signals Today</p>
              <p className="text-lg font-bold tabular-nums">{stats.signalsToday}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
              <TrendingUp className="w-4 h-4 text-primary" />
            </div>
            <div>
              <p className="text-[11px] text-muted-foreground">Trades Today</p>
              <p className="text-lg font-bold tabular-nums">{stats.tradesToday}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Engine Components Table */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm">Engine Components</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs">Component</TableHead>
                <TableHead className="text-xs">Status</TableHead>
                <TableHead className="text-xs">Last Run</TableHead>
                <TableHead className="text-xs">Result</TableHead>
                <TableHead className="text-xs text-right">Runs</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {components.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-muted-foreground text-sm py-8">
                    No engine health data yet. Cron jobs may not have run.
                  </TableCell>
                </TableRow>
              )}
              {components.map((c) => {
                const status = statusColor(c.last_run);
                return (
                  <TableRow key={c.id}>
                    <TableCell className="text-xs font-medium">{c.component}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={`text-[10px] ${status.className}`}>
                        {status.label}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground tabular-nums">{timeAgo(c.last_run)}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{c.last_status ?? '—'}</TableCell>
                    <TableCell className="text-xs text-right tabular-nums">{c.run_count ?? 0}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminHealthCheck;
